import React, { useEffect, useRef, useState } from "react";
import { streamGuide, getGuideHistory } from "../lib/api";
import { Bot, User, Send, X, Sparkles, FilePlus2 } from "lucide-react";

// Minimal markdown: split fenced code blocks from prose.
// onAddCommand(codeText) — when provided, renders an "Add to evidence" action per code block.
function renderContent(text, onAddCommand) {
  const parts = text.split(/```/g);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const body = part.replace(/^[a-zA-Z0-9]*\n/, "");
      return (
        <div key={i} className="my-2">
          <pre className="rounded-md border border-zinc-800 bg-black p-3 overflow-x-auto">
            <code className="font-mono text-[12px] text-emerald-300 whitespace-pre-wrap">{body}</code>
          </pre>
          {onAddCommand && body.trim() && (
            <button
              onClick={() => onAddCommand(body.trim())}
              data-testid="guide-add-evidence"
              className="mt-1 flex items-center gap-1 text-[11px] font-medium text-blue-400 hover:text-blue-300"
            >
              <FilePlus2 className="h-3 w-3" /> Add to task evidence
            </button>
          )}
        </div>
      );
    }
    return (
      <span key={i} className="whitespace-pre-wrap">
        {part.split(/(`[^`]+`)/g).map((seg, j) =>
          seg.startsWith("`") && seg.endsWith("`") ? (
            <code key={j} className="font-mono text-[12px] bg-zinc-800 text-blue-300 px-1.5 py-0.5 rounded">{seg.slice(1, -1)}</code>
          ) : (seg)
        )}
      </span>
    );
  });
}

const STARTERS = [
  "Walk me through this migration from the very beginning.",
  "What do I check right now, and where?",
  "How do I validate the Local Gateway before cutover?",
  "Give me the exact GO/NO-GO smoke tests and commands.",
];

export const RunbookGuide = ({ open, onClose, siteId, siteName, seed, onSeedConsumed, activeTask, onAddCommand }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (open && siteId) getGuideHistory(siteId).then(setMessages).catch(() => {});
  }, [open, siteId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text, extra = {}) => {
    const msg = (text ?? input).trim();
    if (!msg || sendingRef.current) return;
    sendingRef.current = true;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    setSending(true);
    await streamGuide(
      { message: msg, site_id: siteId, phase_name: extra.phase_name || null, task_title: extra.task_title || null },
      {
        onDelta: (delta) => setMessages((m) => {
          const copy = [...m]; const i = copy.length - 1;
          if (copy[i]?.role === "assistant") copy[i] = { ...copy[i], content: copy[i].content + delta };
          return copy;
        }),
        onError: (err) => setMessages((m) => {
          const copy = [...m]; const i = copy.length - 1;
          copy[i] = { role: "assistant", content: "\u26a0\ufe0f Guide error: " + err };
          return copy;
        }),
        onDone: () => {
          setMessages((m) => {
            const copy = [...m]; const i = copy.length - 1;
            if (copy[i]?.role === "assistant") copy[i] = { ...copy[i], streaming: false };
            return copy;
          });
          setSending(false); sendingRef.current = false;
        },
      }
    );
    setSending(false); sendingRef.current = false;
  };

  // Auto-send a seeded question when a task's "Ask AI" button opens the guide.
  useEffect(() => {
    if (open && seed?.message) {
      send(seed.message, { phase_name: seed.phase_name, task_title: seed.task_title });
      onSeedConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end no-print" data-testid="runbook-guide">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:w-[460px] h-full bg-zinc-950 border-l border-zinc-800 flex flex-col shadow-2xl">
        <div className="h-14 border-b border-zinc-800 flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-md bg-blue-600/15 border border-blue-600/30 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-blue-400" />
            </span>
            <div>
              <div className="text-sm font-bold text-white leading-tight">Migration Guide</div>
              <div className="text-[11px] text-zinc-500 leading-tight">{siteName || "Estate"}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="guide-close" className="h-8 w-8 flex items-center justify-center text-zinc-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        {activeTask && (
          <div className="px-4 py-2 border-b border-zinc-800 bg-blue-600/5 text-[11px] text-zinc-400 shrink-0" data-testid="guide-active-task">
            Task: <span className="text-blue-300">{activeTask.title}</span> · commands can be added to its evidence
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
          {messages.length === 0 && (
            <div className="text-center py-6">
              <p className="text-[13px] text-zinc-400 max-w-xs mx-auto">
                Your on-demand walk-through. Ask what to check and I'll tell you the exact command and where — CUCM CLI, RTMT, CUBE, or Webex Control Hub.
              </p>
              <div className="grid gap-2 mt-5 text-left">
                {STARTERS.map((s, i) => (
                  <button key={i} data-testid={`guide-starter-${i}`} onClick={() => send(s)}
                    className="rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-[13px] text-zinc-300 hover:border-zinc-600 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-4">
            {messages.map((m, i) => (
              <div key={i} data-testid={`guide-msg-${m.role}-${i}`} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`h-7 w-7 rounded-md shrink-0 flex items-center justify-center border ${m.role === "user" ? "bg-zinc-800 border-zinc-700" : "bg-blue-600/15 border-blue-600/30"}`}>
                  {m.role === "user" ? <User className="h-3.5 w-3.5 text-zinc-300" /> : <Bot className="h-3.5 w-3.5 text-blue-400" />}
                </div>
                <div className={`rounded-lg border p-3 text-[13px] leading-relaxed max-w-[85%] ${m.role === "user" ? "bg-zinc-800 border-zinc-700 text-white" : "bg-zinc-900 border-zinc-800 text-zinc-200"}`}>
                  {m.role === "assistant" && m.content === "" && m.streaming
                    ? <span className="text-zinc-500 font-mono">analyzing<span className="cursor-blink">_</span></span>
                    : <>{renderContent(m.content, m.role === "assistant" && activeTask ? (cmd) => onAddCommand?.(activeTask.id, cmd) : null)}{m.streaming && <span className="cursor-blink text-blue-400">▍</span>}</>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-zinc-800 p-3 shrink-0">
          <div className="flex gap-2 items-end">
            <textarea
              data-testid="guide-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder="Ask about any step…"
              className="flex-1 resize-none bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2.5 text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-32"
            />
            <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="guide-send-btn"
              className="h-10 w-10 shrink-0 flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md transition-colors">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
